/**
 * Vérifications d'un compte (e-mail, téléphone, pièce d'identité, entreprise) — décisions du 2026-10-17.
 *
 * Ce que chaque action exige est décidé par le serveur (`backend/verifications.py`) et arrive avec l'utilisateur
 * (`user.verifications` : `types` → état de chaque vérification, `required` → les étapes de chaque action, dans
 * l'ordre, `missing` → ce qui manque encore). Un écran ne décide jamais seul qu'une action est permise : il lit
 * `missingFor` / `canDo` ; il ne liste jamais les étapes en dur (`stepsFor`) — le téléphone y apparaîtra seul le jour
 * où le serveur le rendra vérifiable.
 */

export const VERIFIED = 'VERIFIED';

/** Libellés et liens d'affichage, par type de vérification. */
export const VERIFICATION_TYPES = {
  email:    { label: 'Adresse e-mail',      link: '/app/verifications', cta: 'Vérifier mon adresse' },
  phone:    { label: 'Numéro de téléphone', link: '/app/verifications', cta: 'Vérifier mon numéro' },
  identity: { label: "Pièce d'identité",    link: '/app/kyc',           cta: 'Vérifier mon identité' },
  company:  { label: 'Entreprise (SIRET)',  link: '/app/profile',       cta: 'Voir mon entreprise' },
};

/** Libellés d'affichage des actions (les règles sont côté serveur). */
export const ACTION_LABELS = {
  contact: 'écrire à un membre',
  publish: 'publier une annonce',
  propose: 'faire une proposition',
  reserve: 'réserver avec paiement sécurisé',
  create_mission: 'accepter une proposition',
  become_company: 'passer en compte Entreprise',
  subscribe_pro: 'vous abonner à PRO',
  receive_payouts: 'recevoir vos versements',
};

const isAdmin = (user) => (user?.roles || []).includes('ROLE_ADMIN');

/** Vérifications qui manquent à `user` pour `action`, telles que le serveur les a calculées. */
export function missingFor(user, action) {
  if (!user || isAdmin(user)) return [];
  const missing = user.verifications?.missing?.[action];
  if (Array.isArray(missing)) return missing;
  // Profil sans résumé (ancienne réponse) : seule l'identité était exigée
  return user.kyc_status === VERIFIED ? [] : ['identity'];
}

export const canDo = (user, action) => !!user && missingFor(user, action).length === 0;

/** État d'une vérification : 'NONE' | 'PENDING' | 'VERIFIED' | 'REJECTED'. */
export function verificationStatus(user, kind) {
  const s = user?.verifications?.types?.[kind];
  if (s) return s;
  if (kind === 'identity') return { VERIFIED, IN_PROGRESS: 'PENDING', REJECTED: 'REJECTED' }[user?.kyc_status] || 'NONE';
  return 'NONE';
}

/**
 * Coches publiques d'un autre membre, renvoyées par le serveur (`verification_badges` : { email, identity, … }).
 * Réponse plus ancienne sans ce champ : seule l'identité est connue (`is_verified`).
 */
export function badgesOf(member) {
  if (!member) return {};
  if (member.verification_badges) return member.verification_badges;
  return { identity: !!member.is_verified };
}

/** Ce qu'une étape demande, affiché sous son nom. */
export const STEP_HINTS = {
  email: '1 minute · code reçu par e-mail',
  phone: '1 minute · code par WhatsApp ou SMS',
  identity: '2 minutes · validée sous 24 h',
  company: 'SIRET vérifié par notre équipe',
};

/** Pourquoi une action demande un compte vérifié (une phrase, affichée sous le titre). */
export const ACTION_REASONS = {
  contact: 'Les membres échangent avec des comptes dont l’adresse et le numéro sont confirmés.',
  publish: 'Les membres confient leurs colis à des comptes vérifiés.',
  propose: 'Une proposition engage : elle se fait depuis un compte vérifié.',
  reserve: 'Le paiement sécurisé se fait depuis un compte vérifié.',
  create_mission: 'Accepter crée une mission : les deux membres doivent être vérifiés.',
  become_company: 'Un compte Entreprise part d’un compte déjà vérifié.',
  subscribe_pro: 'L’abonnement PRO est réservé aux comptes vérifiés.',
  receive_payouts: 'Les versements ne partent que vers des comptes vérifiés.',
};

// Ordre d'affichage : de la plus rapide à la plus longue
const STEP_ORDER = ['email', 'phone', 'identity', 'company'];

/** Étapes de `action`, dans l'ordre, telles que le serveur les a calculées (repli : celles qui manquent). */
export function requiredFor(user, action) {
  const missing = missingFor(user, action);
  const required = user?.verifications?.required?.[action];
  const all = Array.isArray(required) ? [...required, ...missing.filter((k) => !required.includes(k))] : missing;
  return [...all].sort((a, b) => STEP_ORDER.indexOf(a) - STEP_ORDER.indexOf(b));
}

/**
 * Étapes de `action` avec leur état : 'VERIFIED' (faite) | 'NONE' (à faire) | 'PENDING' (en cours d'examen) |
 * 'REJECTED' (refusée). Une étape manquante n'est jamais affichée comme faite.
 */
export function stepsFor(user, action) {
  const missing = missingFor(user, action);
  return requiredFor(user, action).map((kind) => {
    if (!missing.includes(kind)) return { kind, state: VERIFIED };
    const state = verificationStatus(user, kind);
    return { kind, state: state === VERIFIED ? 'NONE' : state };
  });
}

/** Première étape que l'utilisateur peut faire lui-même (à faire ou refusée) ; null s'il ne reste que de l'attente. */
export function nextStep(steps) {
  return steps.find((s) => s.state === 'NONE' || s.state === 'REJECTED') || null;
}

/**
 * Adresse de retour après une vérification (`?retour=`) : seulement une page de l'application (`/app/…`), jamais une
 * adresse extérieure (un lien piégé ne doit pas pouvoir renvoyer ailleurs).
 */
export function safeReturnPath(path) {
  if (typeof path !== 'string' || !path.startsWith('/app/')) return null;
  if (path.startsWith('//') || path.includes('\\') || path.includes('://') || /[\s<>"']/.test(path)) return null;
  return path;
}

/** Lien d'une étape, avec le retour vers la page d'où l'on vient. */
export function stepLink(kind, from) {
  const link = VERIFICATION_TYPES[kind]?.link || '/app/verifications';
  const back = safeReturnPath(from);
  return back ? `${link}?retour=${encodeURIComponent(back)}` : link;
}
